import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import CONFIG from '@/config/env';

interface Workspace {
  id: string;
  name: string;
}

interface User {
  id: string;
  email: string;
  name: string;
  avatar_url?: string;
  workspaces: Workspace[];
}

export default function CliDeviceAuth() {
  const navigate = useNavigate();
  const [userCode, setUserCode] = useState<string>('');
  const [user, setUser] = useState<User | null>(null);
  const [selectedWorkspace, setSelectedWorkspace] = useState<string | null>(null);
  const [status, setStatus] = useState('Enter your authentication code');
  const [loading, setLoading] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [codeSubmitted, setCodeSubmitted] = useState(false);

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userCode.trim()) {
      toast.error('Please enter the authentication code');
      return;
    }

    try {
      setLoading(true);
      setStatus('Verifying code...');
      
      // Check if user is logged in
      const token = localStorage.getItem('genofx:token');
      
      if (!token) {
        setStatus('Please log in first');
        setLoading(false);
        // Redirect to login with return URL
        setTimeout(() => {
          navigate(`/auth/login?redirect=/auth/cli`);
        }, 1500);
        return;
      }

      // Get user data
      const userResponse = await fetch(`${CONFIG.API_BASE}/api/profile`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!userResponse.ok) {
        throw new Error('Failed to load user data');
      }

      const userData = await userResponse.json() as User;
      setUser(userData);
      
      // Auto-select first workspace if only one
      if (userData.workspaces && userData.workspaces.length === 1) {
        setSelectedWorkspace(userData.workspaces[0].id);
      }
      
      setStatus('Select a workspace to approve');
      setCodeSubmitted(true);
      setLoading(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      setStatus(`Error: ${message}`);
      toast.error(`Failed to verify code: ${message}`);
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedWorkspace || !userCode) {
      toast.error('Please select a workspace');
      return;
    }

    try {
      setIsApproving(true);
      setStatus('Approving...');
      
      const token = localStorage.getItem('genofx:token');
      
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Approve the device code using user code
      const approveResponse = await fetch(`${CONFIG.API_BASE}/api/cli-tokens/device-code/${userCode}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          workspace_id: selectedWorkspace,
          workspace_name: user?.workspaces.find(w => w.id === selectedWorkspace)?.name,
        }),
      });

      if (!approveResponse.ok) {
        const error = await approveResponse.text();
        throw new Error(`Failed to approve device code: ${error}`);
      }

      setStatus('✓ Approved! Check your terminal.');
      toast.success('CLI authentication approved!');
      
      setTimeout(() => {
        window.close();
      }, 2000);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      setStatus(`Error: ${message}`);
      toast.error(`Approval failed: ${message}`);
      setIsApproving(false);
    }
  };

  const handleDeny = async () => {
    if (!userCode) return;

    try {
      setStatus('Denying...');
      
      const token = localStorage.getItem('genofx:token');
      
      if (!token) {
        throw new Error('Not authenticated');
      }

      await fetch(`${CONFIG.API_BASE}/api/cli-tokens/device-code/${userCode}/deny`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      setStatus('✓ Denied. You can close this window.');
      toast.info('CLI authentication denied');
      
      setTimeout(() => {
        window.close();
      }, 2000);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      setStatus(`Error: ${message}`);
      toast.error(`Denial failed: ${message}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 border border-white/20">
            <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          </div>
          <h1 className="text-2xl font-bold text-white">CLI Authentication</h1>
          <p className="text-white/60">{status}</p>
        </div>
      </div>
    );
  }

  // Code input screen
  if (!codeSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-6">
          {/* Header */}
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold text-white">GenOfX CLI</h1>
            <p className="text-white/60">Device Authorization</p>
          </div>

          {/* Instructions */}
          <div className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-3">
            <p className="text-sm text-white/80">
              Enter the authentication code from your terminal:
            </p>
            <form onSubmit={handleCodeSubmit} className="space-y-3">
              <input
                type="text"
                value={userCode}
                onChange={(e) => setUserCode(e.target.value.toUpperCase())}
                placeholder="Enter code (e.g., XXXXXX)"
                maxLength={6}
                className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 text-center text-2xl font-mono tracking-widest focus:outline-none focus:border-[#6E1AF4]"
                autoFocus
              />
              <button
                type="submit"
                className="w-full h-11 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-white font-semibold transition-all"
              >
                Continue
              </button>
            </form>
          </div>

          {/* Status */}
          <p className="text-center text-sm text-white/60">{status}</p>
        </div>
      </div>
    );
  }

  // Workspace selection screen
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center space-y-4 max-w-md">
          <h1 className="text-2xl font-bold text-white">CLI Authentication</h1>
          <p className="text-white/60">{status}</p>
          <p className="text-sm text-white/40">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-white">GenOfX CLI</h1>
          <p className="text-white/60">Device Authorization</p>
        </div>

        {/* Code Display */}
        <div className="bg-white/5 border border-white/10 rounded-lg p-4 text-center">
          <p className="text-xs text-white/40 mb-2">Your authentication code:</p>
          <p className="text-3xl font-mono font-bold text-[#6E1AF4] tracking-widest">{userCode}</p>
        </div>

        {/* User Info */}
        <div className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-3">
          <p className="text-sm text-white/60">Authenticating as:</p>
          <div className="flex items-center gap-3">
            {user.avatar_url && (
              <img 
                src={user.avatar_url} 
                alt={user.name}
                className="w-10 h-10 rounded-full"
              />
            )}
            <div className="text-left">
              <p className="text-white font-medium">{user.name}</p>
              <p className="text-xs text-white/40">{user.email}</p>
            </div>
          </div>
        </div>

        {/* Workspace Selection */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-white/80">Select Workspace:</label>
          <div className="space-y-2">
            {user.workspaces && user.workspaces.length > 0 ? (
              user.workspaces.map((workspace) => (
                <button
                  key={workspace.id}
                  onClick={() => setSelectedWorkspace(workspace.id)}
                  disabled={isApproving}
                  className={`w-full p-3 rounded-lg border transition-all text-left ${
                    selectedWorkspace === workspace.id
                      ? 'bg-[#6E1AF4]/20 border-[#6E1AF4] text-white'
                      : 'bg-white/5 border-white/10 text-white/80 hover:bg-white/10'
                  } ${isApproving ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <p className="font-medium">{workspace.name}</p>
                  <p className="text-xs text-white/40">ID: {workspace.id}</p>
                </button>
              ))
            ) : (
              <p className="text-white/40 text-sm">No workspaces available</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={handleApprove}
            disabled={!selectedWorkspace || isApproving}
            className="flex-1 h-11 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-white/10 disabled:text-white/40 text-white font-semibold transition-all"
          >
            {isApproving ? 'Approving...' : 'Approve'}
          </button>
          <button
            onClick={handleDeny}
            disabled={isApproving}
            className="flex-1 h-11 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-50 text-white font-semibold transition-all"
          >
            Deny
          </button>
        </div>

        {/* Status */}
        <p className="text-center text-sm text-white/60">{status}</p>
      </div>
    </div>
  );
}
